import { format, formatDistanceToNow } from 'date-fns'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type KnowledgeDocumentVersionSummary,
  useKnowledgeDocumentVersion,
  useKnowledgeDocumentVersions,
  useRevertToVersion,
} from '../../../../../../models/knowledgeDocument'

type Props = {
  slug: string
  onBack: () => void
}

export function VersionHistoryPanel({ slug, onBack }: Props) {
  const { data: versions, isLoading } = useKnowledgeDocumentVersions(slug)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const revertMutation = useRevertToVersion()

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back to editor
        </Button>
        <h2 className="text-lg font-semibold">Version history</h2>
        <span />
      </div>

      {isLoading && <Skeleton className="h-64 w-full" />}

      <div className="space-y-2">
        {versions?.map((v) => (
          <VersionRow
            key={v.id}
            version={v}
            slug={slug}
            expanded={expandedId === v.id}
            onToggle={() => setExpandedId(expandedId === v.id ? null : v.id)}
            onRevert={() => revertMutation.mutate({ slug, versionId: v.id })}
            reverting={revertMutation.isPending}
          />
        ))}
      </div>
    </div>
  )
}

function VersionRow({
  version,
  slug,
  expanded,
  onToggle,
  onRevert,
  reverting,
}: {
  version: KnowledgeDocumentVersionSummary
  slug: string
  expanded: boolean
  onToggle: () => void
  onRevert: () => void
  reverting: boolean
}) {
  const { data: full, isLoading } = useKnowledgeDocumentVersion(
    expanded ? slug : null,
    expanded ? version.id : null
  )

  return (
    <div className="rounded-md border">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium">v{version.versionNumber}</span>
          {version.isCurrent && (
            <Badge variant="default" className="text-xs">
              Active
            </Badge>
          )}
          {version.changeNote && (
            <Badge variant="outline" className="text-xs">
              {version.changeNote}
            </Badge>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {formatDistanceToNow(new Date(version.dateCreated), { addSuffix: true })} ·{' '}
          {version.createdByEmail ?? '—'}
        </div>
      </button>

      {expanded && (
        <div className="space-y-2 border-t p-3">
          {isLoading && <Skeleton className="h-32 w-full" />}
          {full && (
            <>
              <div className="text-xs text-gray-500">
                Created at {format(new Date(full.dateCreated), 'PPpp')}
              </div>
              <MarkdownEditor value={full.content} onChange={() => {}} readOnly />
              {!version.isCurrent && (
                <Button size="sm" onClick={onRevert} disabled={reverting}>
                  {reverting ? 'Reverting…' : `Revert to v${version.versionNumber}`}
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
